// ===============================
// algo-astreintes-thoraciques.js
// ===============================
// Algorithme en 3 PHASES interactives pour la génération des astreintes thoraciques.
//
// PHASE 1 : Règles 3+4 — Attribution des blocs weekends/fériés aux intérieurs
//           Étape 0: candidat unique → Étape 1a: souhaits uniques → Étape 1b: optimisation globale
// PHASE 2 : Règle 5 — Attribution Dr Nguyen Yên-lan (1 jeudi/mois)
// PHASE 3 : Règles 6+7 — Jours restants
//           Étape 3a: 1 astreinte/mois par médecin (tous, à plat)
//           Étape 3b: complétion par les intérieurs (charge totale)
// ===============================

// ═══════════════════════════════════════
// CONSTANTES & UTILITAIRES
// ═══════════════════════════════════════

import { getFrenchPublicHolidays } from './frenchHolidays.mjs';

function joursFeriesPourDates(dates) {
  const annees = [...new Set(dates.map(date => Number(String(date).slice(0, 4))))];
  return annees.flatMap(annee => Object.keys(getFrenchPublicHolidays(annee))).sort();
}

function pad2(n) { return String(n).padStart(2, "0"); }

function dateStr(year, month, day) { return `${year}-${pad2(month)}-${pad2(day)}`; }

function parseDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function getDayOfWeek(dateString) { return parseDate(dateString).getDay(); }

function getDayName(dateString) {
  return ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"][getDayOfWeek(dateString)];
}

function getDayNameShort(dateString) {
  return ["dim","lun","mar","mer","jeu","ven","sam"][getDayOfWeek(dateString)];
}

function isJourFerie(dateString, joursFeries) { return joursFeries.includes(dateString); }

function prefLabel(code) {
  switch (code) {
    case 0: return "❌ indisponible";
    case 1: return "⭐ souhaite";
    case 2: return "✅ disponible";
    case 3: return "⚠️ peu dispo";
    default: return "? inconnu";
  }
}

export function medKey(nom, prenom) { return `${nom}_${prenom}`; }

function getPref(desiderataMedecin, ds) {
  const val = desiderataMedecin?.valeurs?.[ds];
  if (!val) return 2;
  const code = typeof val === "object" && val !== null ? val.code : val;
  return typeof code === "number" ? code : 2;
}

function estDisponiblePourBloc(desiderataMedecin, bloc) {
  return bloc.every((jour) => getPref(desiderataMedecin, jour) !== 0);
}

function scorePrefBloc(desiderataMedecin, bloc) {
  let score = 0;
  for (const jour of bloc) {
    const pref = getPref(desiderataMedecin, jour);
    if (pref === 1) score += 0;
    else if (pref === 2) score += 2;
    else if (pref === 3) score += 4;
  }
  return score;
}

function scorePrefJour(desiderataMedecin, jour) {
  const pref = getPref(desiderataMedecin, jour);
  if (pref === 1) return 0;
  if (pref === 2) return 2;
  if (pref === 3) return 4;
  return 100;
}

export function genererTousLesJours(moisList) {
  const jours = [];
  for (const mois of moisList) {
    const [y, m] = mois.split("-").map(Number);
    const nbJours = new Date(y, m, 0).getDate();
    for (let d = 1; d <= nbJours; d++) jours.push(dateStr(y, m, d));
  }
  return jours;
}

function logEquiteTable(logs, label, interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin) {
  logs.push(`  ┌─── ${label} ───`);
  logs.push(`  │ ${"Médecin".padEnd(25)} │ Astr. │ Blocs │  Ratio │ Quotité       │ Blocs attribués`);
  logs.push(`  │${"─".repeat(25)}─┼───────┼───────┼────────┼───────────────┼──────────────────────`);
  const rows = interieursEquite.map((m) => {
    const key = medKey(m.nom, m.prenom);
    const nb = compteurs[key] || 0;
    const ajust = ajustementsEffectifs?.[key] || 0;
    const ratio = ((nb - ajust) / (m.quotite / 100)).toFixed(1);
    const nbBlocs = blocsParMedecin ? (blocsParMedecin[key]?.length || 0) : 0;
    const blocsIndices = blocsParMedecin ? (blocsParMedecin[key] || []) : [];
    return { key, nb, ajust, ratio: parseFloat(ratio), quotite: m.quotite, nbBlocs, blocsIndices };
  });
  rows.sort((a, b) => a.ratio - b.ratio);
  for (const r of rows) {
    const ajustStr = r.ajust !== 0 ? ` ${r.ajust > 0 ? "+" : ""}${r.ajust}` : "";
    const quotStr = `${r.quotite}%${ajustStr}`;
    const blocsNumStr = r.blocsIndices.map(i => `#${i + 1}`).join(", ");
    logs.push(`  │ ${r.key.padEnd(25)} │ ${String(r.nb).padStart(5)} │ ${String(r.nbBlocs).padStart(5)} │ ${r.ratio.toFixed(1).padStart(6)} │ ${quotStr.padEnd(13)} │ ${blocsNumStr}`);
  }
  logs.push(`  └${"─".repeat(95)}`);
}

// ═══════════════════════════════════════
// IDENTIFICATION DES BLOCS (commun)
// ═══════════════════════════════════════

export function identifierBlocs(tousLesJours, joursFeries = joursFeriesPourDates(tousLesJours), logs = null) {
  const joursSet = new Set(tousLesJours);
  const joursEnBloc = new Set();
  const blocs = [];

  if (logs) {
    logs.push("  ┌─────────────────────────────────────────────────────────────");
    logs.push("  │ IDENTIFICATION DES BLOCS (weekends + fériés/ponts)");
    logs.push("  └─────────────────────────────────────────────────────────────");
  }

  // Fériés mercredi → bloc mardi + mercredi
  for (const ferie of joursFeries) {
    if (!joursSet.has(ferie)) continue;
    const dow = getDayOfWeek(ferie);
    if (dow === 3) {
      const d = parseDate(ferie);
      const mardi = new Date(d); mardi.setDate(d.getDate() - 1);
      const mardiStr = dateStr(mardi.getFullYear(), mardi.getMonth() + 1, mardi.getDate());
      if (joursSet.has(mardiStr)) {
        const bloc = [mardiStr, ferie];
        bloc.forEach((j) => joursEnBloc.add(j));
        blocs.push({ jours: bloc, type: "ferie_mercredi" });
        if (logs) logs.push(`  📌 Bloc férié mercredi : ${mardiStr} (mar) + ${ferie} (mer) = 2 jours`);
      }
    }
  }

  // Weekends + extensions fériés
  for (const jour of tousLesJours) {
    if (getDayOfWeek(jour) !== 5) continue;
    const vendredi = parseDate(jour);
    const samedi = new Date(vendredi); samedi.setDate(vendredi.getDate() + 1);
    const dimanche = new Date(vendredi); dimanche.setDate(vendredi.getDate() + 2);
    const venStr = jour;
    const samStr = dateStr(samedi.getFullYear(), samedi.getMonth() + 1, samedi.getDate());
    const dimStr = dateStr(dimanche.getFullYear(), dimanche.getMonth() + 1, dimanche.getDate());
    if (!joursSet.has(samStr) || !joursSet.has(dimStr)) continue;

    const bloc = [venStr, samStr, dimStr];
    const extensions = [];

    const jeudiDate = new Date(vendredi); jeudiDate.setDate(vendredi.getDate() - 1);
    const jeudiStr = dateStr(jeudiDate.getFullYear(), jeudiDate.getMonth() + 1, jeudiDate.getDate());
    const lundiDate = new Date(dimanche); lundiDate.setDate(dimanche.getDate() + 1);
    const lundiStr = dateStr(lundiDate.getFullYear(), lundiDate.getMonth() + 1, lundiDate.getDate());
    const mardiDate = new Date(dimanche); mardiDate.setDate(dimanche.getDate() + 2);
    const mardiStr = dateStr(mardiDate.getFullYear(), mardiDate.getMonth() + 1, mardiDate.getDate());

    if (isJourFerie(venStr, joursFeries) && joursSet.has(jeudiStr) && !joursEnBloc.has(jeudiStr)) {
      bloc.unshift(jeudiStr); extensions.push("+jeu (férié ven)");
    }
    if (isJourFerie(jeudiStr, joursFeries) && joursSet.has(jeudiStr) && !joursEnBloc.has(jeudiStr)) {
      if (!bloc.includes(jeudiStr)) bloc.unshift(jeudiStr);
      extensions.push("+jeu férié (pont ven)");
    }
    if (isJourFerie(lundiStr, joursFeries) && joursSet.has(lundiStr) && !joursEnBloc.has(lundiStr)) {
      bloc.push(lundiStr); extensions.push("+lun férié");
    }
    if (isJourFerie(mardiStr, joursFeries) && joursSet.has(mardiStr) && !joursEnBloc.has(mardiStr)) {
      if (joursSet.has(lundiStr) && !bloc.includes(lundiStr)) { bloc.push(lundiStr); extensions.push("+lun pont"); }
      bloc.push(mardiStr); extensions.push("+mar férié");
    }

    bloc.sort();
    bloc.forEach((j) => joursEnBloc.add(j));
    blocs.push({ jours: bloc, type: extensions.length > 0 ? "weekend_etendu" : "weekend" });

    if (logs) {
      const joursList = bloc.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
      const extLabel = extensions.length > 0 ? ` ← ${extensions.join(", ")}` : "";
      logs.push(`  📅 Bloc ${bloc.length}j : ${joursList}${extLabel}`);
    }
  }

  blocs.sort((a, b) => a.jours[0].localeCompare(b.jours[0]));
  if (logs) {
    logs.push(`  ─────────────────────────────────────────────────────────────`);
    logs.push(`  📊 TOTAL : ${blocs.length} blocs, ${joursEnBloc.size} jours en bloc`);
    logs.push("");
    logs.push(`  ┌─── LISTE DES BLOCS (ordre chronologique) ───`);
    logs.push(`  │ ${"N°".padStart(4)} │ ${"Type".padEnd(16)} │ ${"Jours".padStart(5)} │ Dates`);
    logs.push(`  │${"─".repeat(6)}┼${"─".repeat(18)}┼${"─".repeat(7)}┼${"─".repeat(50)}`);
    for (let i = 0; i < blocs.length; i++) {
      const b = blocs[i];
      const typeLabel = b.type === "weekend" ? "Weekend" : b.type === "weekend_etendu" ? "WE étendu" : "Férié";
      const joursList = b.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
      logs.push(`  │ ${String(`#${i + 1}`).padStart(4)} │ ${typeLabel.padEnd(16)} │ ${String(b.jours.length + "j").padStart(5)} │ ${joursList}`);
    }
    logs.push(`  └${"─".repeat(85)}`);
  }
  return { blocs, joursEnBloc };
}

// ═══════════════════════════════════════
// PRÉPARATION COMMUNE (utilisée par toutes les phases)
// ═══════════════════════════════════════

export function preparerDonnees({ moisList, medecins, desiderataParMois, joursFeries = joursFeriesPourDates(moisList) }) {
  const logs = [];

  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  🏥 ALGORITHME ASTREINTES THORACIQUES — PRÉPARATION");
  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push(`  📅 Quadrimestre : ${moisList.join(", ")}`);
  logs.push(`  👥 Médecins : ${medecins.length} au total`);
  logs.push(`  🗓️  Jours fériés : ${joursFeries.join(", ")}`);

  let interieurs = medecins.filter((m) => m.origine === "interieur");
  let exterieurs = medecins.filter((m) => m.origine === "exterieur");

  logs.push("\n  ┌─── MÉDECINS INTÉRIEURS ───");
  for (const m of interieurs) logs.push(`  │  ${m.nom} ${m.prenom} — ${m.quotite}% — ${m.statut || ""}`);
  logs.push(`  │  TOTAL : ${interieurs.length}`);
  logs.push("  └──────────────────────────────────");
  logs.push("  ┌─── MÉDECINS EXTÉRIEURS ───");
  for (const m of exterieurs) logs.push(`  │  ${m.nom} ${m.prenom} — ${m.quotite}% — ${m.statut || ""}`);
  logs.push(`  │  TOTAL : ${exterieurs.length}`);
  logs.push("  └──────────────────────────────────");

  // Nguyen
  const nguyenMed = interieurs.find(
    (m) => m.nom.toLowerCase() === "nguyen" && m.prenom.toLowerCase().includes("lan")
  );
  const nguyenMedKey = nguyenMed ? medKey(nguyenMed.nom, nguyenMed.prenom) : null;
  if (nguyenMedKey) logs.push(`  🔒 RÈGLE 5 : Dr ${nguyenMed.nom} ${nguyenMed.prenom} → 1 jeudi/mois`);

  // Intérieurs équité (sans Nguyen)
  let interieursEquite = interieurs.filter((m) => medKey(m.nom, m.prenom) !== nguyenMedKey);

  // Fusionner desiderata
  const desiderataParMedecin = {};
  // Tracker les mois pour lesquels chaque médecin a effectivement soumis des desiderata
  const moisSoumisParMedecin = {};
  for (const mois of moisList) {
    for (const d of (desiderataParMois[mois] || [])) {
      if (!d?.nom || !d?.prenom) continue;
      const key = medKey(d.nom, d.prenom);
      if (!desiderataParMedecin[key]) desiderataParMedecin[key] = { nom: d.nom, prenom: d.prenom, valeurs: {} };
      if (!moisSoumisParMedecin[key]) moisSoumisParMedecin[key] = new Set();
      moisSoumisParMedecin[key].add(mois);
      for (const [date, val] of Object.entries(d.valeurs || {})) {
        desiderataParMedecin[key].valeurs[date] = val;
      }
    }
  }

  // Pour les mois non soumis : marquer tous les jours comme indisponibles (code 0)
  // Cela évite qu'un médecin ayant donné ses desiderata pour 1 mois seulement
  // soit considéré comme "disponible" les autres mois par défaut
  for (const [key, moisSet] of Object.entries(moisSoumisParMedecin)) {
    const moisManquants = moisList.filter(m => !moisSet.has(m));
    if (moisManquants.length > 0) {
      logs.push(`  ⚠️ ${key} : desiderata soumis pour ${[...moisSet].join(", ")} uniquement → mois manquants marqués indisponibles : ${moisManquants.join(", ")}`);
      for (const mois of moisManquants) {
        const [y, mo] = mois.split("-").map(Number);
        const nbJours = new Date(y, mo, 0).getDate();
        for (let d = 1; d <= nbJours; d++) {
          const ds = dateStr(y, mo, d);
          // Ne marquer indisponible que si aucune valeur n'est déjà présente
          if (!desiderataParMedecin[key].valeurs[ds]) {
            desiderataParMedecin[key].valeurs[ds] = { code: 0, label: "Indisponible (mois non soumis)" };
          }
        }
      }
    }
  }

  logs.push("\n  ┌─── DESIDERATA CHARGÉS ───");
  for (const [key, data] of Object.entries(desiderataParMedecin)) {
    const moisSoumis = moisSoumisParMedecin[key] ? [...moisSoumisParMedecin[key]].join(", ") : "aucun";
    logs.push(`  │  ${key} : ${Object.keys(data.valeurs).length} jours renseignés (mois soumis: ${moisSoumis})`);
  }
  logs.push("  └──────────────────────────────────");

  // Détecter les médecins SANS desiderata et les exclure de l'attribution automatique
  const sansDesi = [];
  const allMedKeys = [...interieurs, ...exterieurs].map(m => medKey(m.nom, m.prenom));
  const desidKeys = Object.keys(desiderataParMedecin);
  logs.push("\n  ┌─── 🔍 MATCHING MÉDECINS ↔ DESIDERATA ───");
  logs.push(`  │  Clés médecins (${allMedKeys.length}) : ${allMedKeys.join(", ")}`);
  logs.push(`  │  Clés desiderata (${desidKeys.length}) : ${desidKeys.join(", ")}`);
  for (const key of allMedKeys) {
    const found = !!desiderataParMedecin[key];
    if (!found) logs.push(`  │  ❌ ${key} — PAS de match dans desiderata`);
  }
  logs.push("  └──────────────────────────────────");
  for (const key of allMedKeys) {
    if (!desiderataParMedecin[key]) {
      sansDesi.push(key);
    }
  }
  if (sansDesi.length > 0) {
    logs.push("\n  ┌─── ⚠️ MÉDECINS SANS DESIDERATA — EXCLUS DE L'ATTRIBUTION ───");
    for (const key of sansDesi) {
      logs.push(`  │  🚫 ${key} — pas de desiderata soumis → exclu de l'algorithme`);
    }
    logs.push("  └──────────────────────────────────");
    interieurs = interieurs.filter(m => !sansDesi.includes(medKey(m.nom, m.prenom)));
    exterieurs = exterieurs.filter(m => !sansDesi.includes(medKey(m.nom, m.prenom)));
    interieursEquite = interieursEquite.filter(m => !sansDesi.includes(medKey(m.nom, m.prenom)));
  }

  // Tous les jours
  const tousLesJours = genererTousLesJours(moisList);
  logs.push(`\n  📊 Total jours quadrimestre : ${tousLesJours.length}`);

  // Blocs
  logs.push("");
  const { blocs, joursEnBloc } = identifierBlocs(tousLesJours, joursFeries, logs);
  const joursRestants = tousLesJours.filter((j) => !joursEnBloc.has(j));
  logs.push(`  📊 Jours hors blocs (semaine) : ${joursRestants.length}\n`);

  // Compteurs initiaux
  const compteurs = {};
  const compteursParMois = {};
  for (const m of medecins) {
    const key = medKey(m.nom, m.prenom);
    compteurs[key] = 0;
    compteursParMois[key] = {};
    for (const mois of moisList) compteursParMois[key][mois] = 0;
  }

  // Log final : liste exacte des intérieurs équité (diagnostic)
  logs.push("  ┌─── INTÉRIEURS ÉQUITÉ (PARTICIPANTS PHASE 1) ───");
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    const hasDesi = !!desiderataParMedecin[key];
    const nbJoursDesi = hasDesi ? Object.keys(desiderataParMedecin[key].valeurs).length : 0;
    logs.push(`  │  ${key} (${m.quotite}%) — desiderata: ${hasDesi ? `✅ ${nbJoursDesi}j` : "❌ ABSENT"}`);
  }
  logs.push(`  │  TOTAL : ${interieursEquite.length} médecins`);
  logs.push("  └──────────────────────────────────");

  return {
    interieurs, exterieurs, interieursEquite,
    nguyenMed, nguyenMedKey,
    desiderataParMedecin, medecinsSansDesiderata: sansDesi,
    tousLesJours, blocs, joursEnBloc, joursRestants,
    compteurs, compteursParMois,
    logs,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 1 : Attribution des blocs weekends/fériés aux intérieurs
// Étape 0: candidat unique → Étape 1a: souhaits uniques → Étape 1b: optimisation globale
// ═══════════════════════════════════════════════════════════════════════

export function phase1_attribuerBlocs({ prepData, ajustements = {}, mode = "standard" }) {
  const {
    interieursEquite, exterieurs, desiderataParMedecin, blocs,
    compteurs, compteursParMois,
  } = prepData;

  const ESPACEMENT_MIN = 3;
  const modeGlobal = mode === "global";

  const ajustementsEffectifs = {};
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    ajustementsEffectifs[key] = ajustements[key] || 0;
  }

  const logs = [];
  const affectationsBlocs = {};

  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push(`  📌 PHASE 1 : Attribution des blocs WE/fériés aux intérieurs [mode ${modeGlobal ? "GLOBAL" : "STANDARD"}]`);
  logs.push(modeGlobal
    ? "  📌 Étape 0: candidat unique → Optimisation globale (souhaite + disponible)"
    : "  📌 Étape 0: candidat unique → Étape 1a: souhaits uniques → Étape 1b: optimisation globale");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  const hasAjust = Object.values(ajustementsEffectifs).some(v => v !== 0);
  if (hasAjust) {
    logs.push("  ┌─── AJUSTEMENTS BONUS/MALUS ───");
    for (const [key, val] of Object.entries(ajustementsEffectifs)) {
      if (val !== 0) logs.push(`  │  ${key} : ${val > 0 ? "+" : ""}${val}`);
    }
    logs.push("  └──────────────────────────────────");
  } else {
    logs.push("  ℹ️ Aucun ajustement bonus/malus défini");
  }

  const totalJoursBlocs = blocs.reduce((sum, b) => sum + b.jours.length, 0);
  logs.push(`  📊 ${blocs.length} blocs, ${totalJoursBlocs} jours en bloc`);
  const repartition = {};
  for (const b of blocs) { repartition[b.jours.length + "j"] = (repartition[b.jours.length + "j"] || 0) + 1; }
  logs.push(`  📊 Tailles : ${Object.entries(repartition).map(([k, v]) => `${v}×${k}`).join(", ")}`);

  // Nombre cible de blocs par médecin
  const totalQuotite = interieursEquite.reduce((s, m) => s + m.quotite, 0);
  logs.push(`  📊 Quotité cumulée intérieurs : ${totalQuotite}%`);
  const cibleParMed = {};
  const plafondParMed = {};
  const plancherParMed = {};
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    const ajust = ajustementsEffectifs[key] || 0;
    const cible = (m.quotite / totalQuotite) * blocs.length;
    cibleParMed[key] = cible;
    plafondParMed[key] = Math.ceil(cible);
    plancherParMed[key] = Math.max(1, Math.floor(cible));
    logs.push(`     ${key} (${m.quotite}%${ajust !== 0 ? `, ajust=${ajust > 0 ? "+" : ""}${ajust}` : ""}) : cible ~${cible.toFixed(1)} blocs, plancher=${plancherParMed[key]}, plafond=${plafondParMed[key]}`);
  }

  // Tracker des blocs par médecin
  const blocsParMedecin = {};
  for (const m of interieursEquite) {
    blocsParMedecin[medKey(m.nom, m.prenom)] = [];
  }
  const affectationParBloc = new Array(blocs.length).fill(null);

  // Helper : espacement minimum d'un médecin par rapport à un bloc
  const getEspacementMin = (medKeyStr, blocIdx) => {
    const indices = blocsParMedecin[medKeyStr] || [];
    if (indices.length === 0) return 999;
    let minDist = 999;
    for (const idx of indices) {
      minDist = Math.min(minDist, Math.abs(blocIdx - idx));
    }
    return minDist;
  };

  // Helper : ratio d'équité d'un médecin (Phase 1 = sans ajustement, pour distribution équitable des blocs)
  // Les ajustements seront pris en compte en Phase 2 (jours individuels) pour la correction fine
  const getRatio = (key) => {
    const m = interieursEquite.find(mm => medKey(mm.nom, mm.prenom) === key);
    if (!m) return 999;
    return ((compteurs[key] || 0)) / (m.quotite / 100);
  };

  // Helper : meilleur sous-ensemble de blocs maximisant l'espacement minimum
  const bestSpacedSubset = (candidateIndices, maxCount, existingIndices) => {
    const sorted = [...candidateIndices].sort((a, b) => a - b);
    const n = sorted.length;
    if (n === 0) return [];
    let bestCombo = [];
    let bestMinEsp = -1;
    for (let k = Math.min(maxCount, n); k >= 1; k--) {
      const search = (start, chosen) => {
        if (chosen.length === k) {
          const all = [...existingIndices, ...chosen].sort((a, b) => a - b);
          let minEsp = all.length > 1 ? Infinity : 999;
          for (let i = 1; i < all.length; i++) {
            minEsp = Math.min(minEsp, all[i] - all[i - 1]);
          }
          if (minEsp >= ESPACEMENT_MIN && minEsp > bestMinEsp) {
            bestMinEsp = minEsp;
            bestCombo = [...chosen];
          }
          return;
        }
        for (let i = start; i <= n - (k - chosen.length); i++) {
          search(i + 1, [...chosen, sorted[i]]);
        }
      };
      search(0, []);
      if (bestCombo.length > 0) break;
    }
    return bestCombo;
  };

  // Helper : attribuer un bloc à un médecin
  const attribuerBloc = (blocIdx, choisiKey) => {
    const bloc = blocs[blocIdx];
    for (const j of bloc.jours) {
      affectationsBlocs[j] = choisiKey;
      compteurs[choisiKey]++;
      const moisDuJour = j.substring(0, 7);
      if (compteursParMois[choisiKey]?.[moisDuJour] !== undefined) compteursParMois[choisiKey][moisDuJour]++;
    }
    affectationParBloc[blocIdx] = choisiKey;
    blocsParMedecin[choisiKey].push(blocIdx);
  };

  // Helper : trouver les souhaiteurs "souhaite absolument" d'un bloc
  const trouverSouhaiteurs = (blocIdx) => {
    const bloc = blocs[blocIdx];
    const result = [];
    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      if (!estDisponiblePourBloc(desid, bloc.jours)) continue;
      const joursWE = bloc.jours.filter(j => {
        const d = new Date(j + "T00:00:00");
        const dow = d.getDay();
        return dow === 0 || dow === 6;
      });
      const joursAVerifier = joursWE.length > 0 ? joursWE : bloc.jours;
      if (!joursAVerifier.every(j => getPref(desid, j) === 1)) continue;
      result.push({ key, nom: m.nom, prenom: m.prenom, quotite: m.quotite });
    }
    return result;
  };

  // ═══════════════════════════════════════════════════════════
  // ÉTAPE 0 : Pré-attribution des blocs à candidat unique
  // ═══════════════════════════════════════════════════════════
  logs.push("\n  ┌─────────────────────────────────────────────────────────────");
  logs.push("  │ ÉTAPE 0 : Pré-attribution des blocs à candidat unique");
  logs.push("  │ Si un seul médecin est éligible → attribution automatique");
  logs.push("  └─────────────────────────────────────────────────────────────");

  let nbPreAttrib = 0;
  let nbPreLibre = 0;
  const blocsLaisseLibres = new Set();
  for (let blocIdx = 0; blocIdx < blocs.length; blocIdx++) {
    const bloc = blocs[blocIdx];
    const candidatsUniques = [];
    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      if (estDisponiblePourBloc(desid, bloc.jours)) {
        candidatsUniques.push({ key, nom: m.nom, prenom: m.prenom });
      }
    }

    if (candidatsUniques.length === 1) {
      const choisi = candidatsUniques[0];
      const joursList = bloc.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");

      const espacement = getEspacementMin(choisi.key, blocIdx);
      if (espacement < ESPACEMENT_MIN) {
        logs.push(`  🚫 BLOC ${blocIdx + 1}/${blocs.length} [${bloc.jours.length}j] : ${joursList}`);
        logs.push(`     → Candidat unique : ${choisi.key}, MAIS espacement insuffisant (esp=${espacement}, min=${ESPACEMENT_MIN})`);
        logs.push(`     → Bloc laissé LIBRE pour attribution manuelle`);
        for (const j of bloc.jours) affectationsBlocs[j] = null;
        affectationParBloc[blocIdx] = null;
        blocsLaisseLibres.add(blocIdx);
        nbPreLibre++;
        continue;
      }

      const espLabel = espacement === 999 ? "1er bloc" : `esp=${espacement}`;
      const autresBlocs = blocsParMedecin[choisi.key].map(i => `#${i + 1}`).join(", ");
      const blocsLabel = autresBlocs ? ` | blocs: ${autresBlocs}` : "";
      logs.push(`  ✅ BLOC ${blocIdx + 1}/${blocs.length} [${bloc.jours.length}j] : ${joursList}`);
      logs.push(`     → Candidat unique : ${choisi.key} — attribution automatique (${espLabel}${blocsLabel})`);

      attribuerBloc(blocIdx, choisi.key);
      nbPreAttrib++;
    }
  }

  if (nbPreAttrib === 0 && nbPreLibre === 0) {
    logs.push("  ℹ️ Aucun bloc à candidat unique — tous les blocs ont 2+ candidats");
  } else {
    if (nbPreAttrib > 0) logs.push(`\n  📊 ${nbPreAttrib} bloc(s) pré-attribué(s) (candidat unique)`);
    if (nbPreLibre > 0) logs.push(`  ⚠️ ${nbPreLibre} bloc(s) reporté(s) en 1b (candidat unique mais espacement insuffisant — sera réessayé)`);
    logs.push(`  📊 ${blocs.length - nbPreAttrib} bloc(s) restant(s) pour les étapes 1a/1b (dont ${nbPreLibre} reporté(s) de l'étape 0)`);
  }
  logs.push("");
  logEquiteTable(logs, "ÉQUITÉ APRÈS ÉTAPE 0", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);

  // ═══════════════════════════════════════════════════════════
  // ÉTAPE 1a : Souhaits uniques sans conflit — meilleur espacement, plancher
  // Pour chaque médecin : trouver les blocs où il est SEUL souhaiteur
  // Sélectionner les N meilleurs blocs (N = plancher) maximisant l'espacement
  // ═══════════════════════════════════════════════════════════
  let nbAttrib1a = 0;

  if (modeGlobal) {
    // MODE GLOBAL : On saute l'étape 1a — tout sera géré dans l'optimisation globale
    logs.push("\n  ┌─────────────────────────────────────────────────────────────");
    logs.push("  │ ÉTAPE 1a : IGNORÉE (mode GLOBAL)");
    logs.push("  │ Tous les blocs seront traités dans l'optimisation globale");
    logs.push("  │ (souhaite absolument + disponible traités ensemble)");
    logs.push("  └─────────────────────────────────────────────────────────────");

    const nbRestantsApres1a = blocs.filter((_, i) => affectationParBloc[i] === null).length;
    logs.push(`  📊 ${nbRestantsApres1a} bloc(s) restant(s) pour l'optimisation globale`);
    logs.push("");
  } else {
    // MODE STANDARD : Étape 1a classique (souhaits uniques)
    logs.push("\n  ┌─────────────────────────────────────────────────────────────");
    logs.push("  │ ÉTAPE 1a : Souhaits uniques sans conflit");
    logs.push("  │ Meilleure combinaison espacée, plancher par médecin");
    logs.push("  └─────────────────────────────────────────────────────────────");

    // Collecter les blocs par souhaiteur unique
    const soleSouhaitsParDoc = {};
    for (let blocIdx = 0; blocIdx < blocs.length; blocIdx++) {
      if (affectationParBloc[blocIdx] !== null) continue;
      if (blocsLaisseLibres.has(blocIdx)) continue;
      const souhaiteurs = trouverSouhaiteurs(blocIdx);
      if (souhaiteurs.length === 1) {
        const key = souhaiteurs[0].key;
        if (!soleSouhaitsParDoc[key]) soleSouhaitsParDoc[key] = [];
        soleSouhaitsParDoc[key].push(blocIdx);
      }
    }

    for (const [docKey, candidateBlocs] of Object.entries(soleSouhaitsParDoc)) {
      const currentBlocCount = blocsParMedecin[docKey].length;
      const plancher = plancherParMed[docKey];
      const remaining = plancher - currentBlocCount;

      const blocsListStr = candidateBlocs.map(i => `#${i + 1}`).join(", ");
      logs.push(`\n  ┌─── ${docKey} : ${candidateBlocs.length} bloc(s) en souhait unique [${blocsListStr}], plancher=${plancher}, déjà=${currentBlocCount} ───`);

      if (remaining <= 0) {
        logs.push(`  │  ⚠️ Plancher déjà atteint (${currentBlocCount}/${plancher}) — souhaits reportés en Étape 1b pour équilibrage`);
        logs.push(`  └──────────────────────────────────`);
        continue;
      }

      const selected = bestSpacedSubset(candidateBlocs, remaining, blocsParMedecin[docKey]);

      if (selected.length === 0) {
        logs.push(`  │  ⚠️ Aucune combinaison valide (espacement < ${ESPACEMENT_MIN}) — reporté en Étape 1b`);
        logs.push(`  └──────────────────────────────────`);
        continue;
      }

      logs.push(`  │  ✅ Sélection optimale : ${selected.map(i => `#${i + 1}`).join(", ")} (espacement maximal)`);
      const nonSelected = candidateBlocs.filter(i => !selected.includes(i));
      if (nonSelected.length > 0) {
        logs.push(`  │  📋 Non retenus : ${nonSelected.map(i => `#${i + 1}`).join(", ")} → reportés en Étape 1b`);
      }

      for (const blocIdx of selected) {
        const bloc = blocs[blocIdx];
        const joursList = bloc.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
        const esp = getEspacementMin(docKey, blocIdx);
        const espLabel = esp === 999 ? "1er bloc" : `esp=${esp}`;
        const autresBlocs = blocsParMedecin[docKey].map(i => `#${i + 1}`).join(", ");
        const blocsLabel = autresBlocs ? ` | blocs: ${autresBlocs}` : "";
        logs.push(`  │  🏆 BLOC #${blocIdx + 1} [${bloc.jours.length}j] : ${joursList} → ${docKey} (${espLabel}${blocsLabel})`);
        attribuerBloc(blocIdx, docKey);
        nbAttrib1a++;
      }
      logs.push(`  └──────────────────────────────────`);
    }

    if (nbAttrib1a > 0) {
      logs.push(`\n  📊 Étape 1a : ${nbAttrib1a} bloc(s) attribué(s) (souhaits uniques, espacement optimal)`);
    } else {
      logs.push("\n  ℹ️ Étape 1a : aucun bloc à souhaiteur unique");
    }

    const nbRestantsApres1a = blocs.filter((_, i) => affectationParBloc[i] === null).length;
    logs.push(`  📊 ${nbRestantsApres1a} bloc(s) restant(s) pour l'étape 1b`);
    logs.push("");
    logEquiteTable(logs, "ÉQUITÉ APRÈS ÉTAPE 1a", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);
  }

  // ═══════════════════════════════════════════════════════════
  // ÉTAPE 1b : Optimisation GLOBALE — tous les blocs restants
  // Approche médecin-centrique en 2 passes :
  //   Passe 1 : Amener TOUS les médecins au plancher (distribution homogène)
  //   Passe 2 : Distribuer les blocs restants jusqu'au plafond
  // Puis optimisation par échanges pour convergence globale
  // ═══════════════════════════════════════════════════════════
  logs.push("\n  ┌─────────────────────────────────────────────────────────────");
  logs.push("  │ ÉTAPE 1b : Optimisation GLOBALE (souhaite + disponible)");
  logs.push("  │ Passe 1 : amener tous les médecins au plancher");
  logs.push("  │ Passe 2 : distribuer les restants jusqu'au plafond");
  logs.push("  │ Vision sur l'ensemble du quadrimestre");
  logs.push("  └─────────────────────────────────────────────────────────────");

  // Fonction commune pour un tour de round-robin avec une limite donnée
  const roundRobinAvecLimite = (limiteParMed, labelPasse) => {
    let nbAttrib = 0;
    let roundContinue = true;
    while (roundContinue) {
      roundContinue = false;

      const docRatios = interieursEquite.map(m => {
        const key = medKey(m.nom, m.prenom);
        return { key, ratio: getRatio(key), blocCount: blocsParMedecin[key].length, limite: limiteParMed[key], quotite: m.quotite };
      });
      docRatios.sort((a, b) => {
        if (a.ratio !== b.ratio) return a.ratio - b.ratio;
        return a.key.localeCompare(b.key);
      });

      for (const doc of docRatios) {
        if (doc.blocCount >= doc.limite) continue;

        const eligibleBlocs = [];
        for (let blocIdx = 0; blocIdx < blocs.length; blocIdx++) {
          if (affectationParBloc[blocIdx] !== null) continue;
          // Les blocs laissés libres en Étape 0 sont réessayés ici
          const desid = desiderataParMedecin[doc.key];
          if (!estDisponiblePourBloc(desid, blocs[blocIdx].jours)) continue;
          if (getEspacementMin(doc.key, blocIdx) < ESPACEMENT_MIN) continue;
          const score = scorePrefBloc(desid, blocs[blocIdx].jours);
          const esp = getEspacementMin(doc.key, blocIdx);
          eligibleBlocs.push({ blocIdx, score, esp });
        }

        if (eligibleBlocs.length === 0) continue;

        eligibleBlocs.sort((a, b) => {
          if (a.score !== b.score) return a.score - b.score;
          return b.esp - a.esp;
        });

        const best = eligibleBlocs[0];
        const bloc = blocs[best.blocIdx];
        const joursList = bloc.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
        const espLabel = best.esp === 999 ? "1er bloc" : `esp=${best.esp}`;
        const autresBlocs = blocsParMedecin[doc.key].map(i => `#${i + 1}`).join(", ");
        const blocsLabel = autresBlocs ? ` | blocs: ${autresBlocs}` : "";
        const prefLbl = best.score === 0 ? "⭐souhaite" : best.score <= 6 ? "✅dispo" : "⚠️peu dispo";
        logs.push(`  │  🏆 [${labelPasse}] BLOC #${best.blocIdx + 1} [${bloc.jours.length}j] : ${joursList} → ${doc.key} (ratio=${doc.ratio.toFixed(2)}, ${espLabel}, ${prefLbl}${blocsLabel})`);

        attribuerBloc(best.blocIdx, doc.key);
        nbAttrib++;
        roundContinue = true;
        break;
      }
    }
    return nbAttrib;
  };

  // Passe 1 : amener tout le monde au plancher
  logs.push("\n  │  📋 Passe 1 — Objectif : plancher pour tous");
  const nbAttrib1b_passe1 = roundRobinAvecLimite(plancherParMed, "plancher");
  logs.push(`  │  📊 Passe 1 : ${nbAttrib1b_passe1} bloc(s) attribué(s)`);

  // Passe 2 : distribuer les restants jusqu'au plafond
  logs.push("\n  │  📋 Passe 2 — Objectif : compléter jusqu'au plafond");
  const nbAttrib1b_passe2 = roundRobinAvecLimite(plafondParMed, "plafond");
  logs.push(`  │  📊 Passe 2 : ${nbAttrib1b_passe2} bloc(s) attribué(s)`);

  const nbAttrib1b = nbAttrib1b_passe1 + nbAttrib1b_passe2;

  // Signaler les blocs restants non attribués → laissés libres pour le responsable
  let nbBlocsLibres = 0;
  for (let blocIdx = 0; blocIdx < blocs.length; blocIdx++) {
    if (affectationParBloc[blocIdx] !== null) continue;

    const bloc = blocs[blocIdx];
    const joursList = bloc.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");

    // Lister les raisons pour lesquelles ce bloc n'a pas pu être attribué
    const raisons = [];
    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      if (!estDisponiblePourBloc(desid, bloc.jours)) {
        raisons.push(`${key}: indisponible`);
      } else if (getEspacementMin(key, blocIdx) < ESPACEMENT_MIN) {
        raisons.push(`${key}: esp=${getEspacementMin(key, blocIdx)}<${ESPACEMENT_MIN}`);
      } else if (blocsParMedecin[key].length >= plafondParMed[key]) {
        raisons.push(`${key}: plafond atteint (${blocsParMedecin[key].length}/${plafondParMed[key]})`);
      }
    }

    logs.push(`  │  🚫 BLOC #${blocIdx + 1} [${bloc.jours.length}j] : ${joursList} — NON ATTRIBUÉ → laissé libre`);
    if (raisons.length > 0) {
      logs.push(`  │     Raisons : ${raisons.join(" | ")}`);
    }
    for (const j of bloc.jours) affectationsBlocs[j] = null;
    affectationParBloc[blocIdx] = null;
    nbBlocsLibres++;
  }

  if (nbBlocsLibres > 0) {
    logs.push(`  │  ⚠️ ${nbBlocsLibres} bloc(s) laissé(s) libre(s) pour attribution manuelle par le responsable`);
  }

  logs.push(`\n  📊 Étape 1b : ${nbAttrib1b} bloc(s) attribué(s) (optimisation globale)`);
  logs.push("");
  logEquiteTable(logs, "ÉQUITÉ APRÈS ÉTAPE 1b (AVANT SWAPS)", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);

  // Optimisation par échanges (swaps)
  logs.push("");
  logs.push("  ┌─────────────────────────────────────────────────────────────");
  logs.push("  │ Optimisation par échanges (équité + espacement)");
  logs.push("  └─────────────────────────────────────────────────────────────");

  const hasConsecutifs = (indices) => {
    const sorted = [...indices].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] - sorted[i - 1] < ESPACEMENT_MIN) return true;
    }
    return false;
  };

  const computeEcart = () => {
    const ratios = interieursEquite.map(m => getRatio(medKey(m.nom, m.prenom)));
    return Math.max(...ratios) - Math.min(...ratios);
  };

  let swapsEffectues = 0;
  let swapIteration = 0;
  while (swapIteration < 100) {
    swapIteration++;
    let meilleurSwap = null;
    let meilleurGain = 0;
    const ecartActuel = computeEcart();

    for (let i = 0; i < blocs.length; i++) {
      for (let j = i + 1; j < blocs.length; j++) {
        const medI = affectationParBloc[i];
        const medJ = affectationParBloc[j];
        if (!medI || !medJ || medI === medJ) continue;

        const desidI = desiderataParMedecin[medI];
        const desidJ = desiderataParMedecin[medJ];
        if (!estDisponiblePourBloc(desidI, blocs[j].jours)) continue;
        if (!estDisponiblePourBloc(desidJ, blocs[i].jours)) continue;

        const newIndicesI = blocsParMedecin[medI].filter(idx => idx !== i).concat([j]);
        const newIndicesJ = blocsParMedecin[medJ].filter(idx => idx !== j).concat([i]);
        if (hasConsecutifs(newIndicesI) || hasConsecutifs(newIndicesJ)) continue;

        const tailleI = blocs[i].jours.length;
        const tailleJ = blocs[j].jours.length;
        const newCountI = (compteurs[medI] || 0) - tailleI + tailleJ;
        const newCountJ = (compteurs[medJ] || 0) - tailleJ + tailleI;

        // Phase 1 swaps : sans ajustement (équité brute basée sur quotité)
        const newRatios = interieursEquite.map(m => {
          const key = medKey(m.nom, m.prenom);
          if (key === medI) return newCountI / (m.quotite / 100);
          if (key === medJ) return newCountJ / (m.quotite / 100);
          return ((compteurs[key] || 0)) / (m.quotite / 100);
        });
        const newEcart = Math.max(...newRatios) - Math.min(...newRatios);
        const gain = ecartActuel - newEcart;

        if (gain > meilleurGain + 0.01) {
          meilleurGain = gain;
          meilleurSwap = { i, j, medI, medJ, tailleI, tailleJ, gain };
        }
      }
    }

    if (!meilleurSwap) {
      logs.push(`  ℹ️ Aucun échange améliorant (itération ${swapIteration})`);
      break;
    }

    const { i, j, medI, medJ, tailleI, tailleJ, gain } = meilleurSwap;
    for (const jour of blocs[i].jours) { affectationsBlocs[jour] = medJ; }
    for (const jour of blocs[j].jours) { affectationsBlocs[jour] = medI; }
    compteurs[medI] = (compteurs[medI] || 0) - tailleI + tailleJ;
    compteurs[medJ] = (compteurs[medJ] || 0) - tailleJ + tailleI;
    for (const jour of blocs[i].jours) {
      const moisDuJour = jour.substring(0, 7);
      if (compteursParMois[medI]?.[moisDuJour] !== undefined) compteursParMois[medI][moisDuJour]--;
      if (compteursParMois[medJ]?.[moisDuJour] !== undefined) compteursParMois[medJ][moisDuJour]++;
    }
    for (const jour of blocs[j].jours) {
      const moisDuJour = jour.substring(0, 7);
      if (compteursParMois[medJ]?.[moisDuJour] !== undefined) compteursParMois[medJ][moisDuJour]--;
      if (compteursParMois[medI]?.[moisDuJour] !== undefined) compteursParMois[medI][moisDuJour]++;
    }
    blocsParMedecin[medI] = blocsParMedecin[medI].filter(idx => idx !== i).concat([j]).sort((a, b) => a - b);
    blocsParMedecin[medJ] = blocsParMedecin[medJ].filter(idx => idx !== j).concat([i]).sort((a, b) => a - b);
    affectationParBloc[i] = medJ;
    affectationParBloc[j] = medI;

    swapsEffectues++;
    logs.push(`  🔄 SWAP #${swapsEffectues} : ${medI}(bloc#${i + 1},${tailleI}j)↔${medJ}(bloc#${j + 1},${tailleJ}j) | gain=${gain.toFixed(1)}`);
  }

  if (swapsEffectues === 0) {
    logs.push(`  ✅ Attribution initiale déjà optimale`);
  } else {
    logs.push(`\n  📊 ${swapsEffectues} échange(s) effectué(s)`);
  }

  // ═══════════════════════════════════════════════════════════
  // RÉÉQUILIBRAGE PAR TRANSFERT
  // Pour tout déséquilibre >= 2 blocs entre deux médecins,
  // tenter de transférer un bloc du sur-servi au sous-servi.
  // Prend en compte les préférences (souhaite > dispo).
  // ═══════════════════════════════════════════════════════════
  logs.push("");
  logs.push("  ┌─────────────────────────────────────────────────────────────");
  logs.push("  │ Rééquilibrage par TRANSFERT (homogénéiser le nb de blocs)");
  logs.push("  │ Analyse tout déséquilibre ≥ 2 blocs entre deux médecins");
  logs.push("  └─────────────────────────────────────────────────────────────");

  let transfertsEffectues = 0;
  let transfertContinue = true;
  while (transfertContinue) {
    transfertContinue = false;

    // Calculer l'état actuel de chaque médecin
    const docBlocs = interieursEquite.map(m => {
      const key = medKey(m.nom, m.prenom);
      return { key, blocCount: blocsParMedecin[key].length, ratio: getRatio(key), quotite: m.quotite };
    });
    docBlocs.sort((a, b) => a.blocCount - b.blocCount);

    const minBlocs = docBlocs[0].blocCount;
    const maxBlocs = docBlocs[docBlocs.length - 1].blocCount;

    if (maxBlocs - minBlocs < 2) {
      logs.push(`  ℹ️ Distribution homogène (écart max en blocs : ${maxBlocs - minBlocs}) — aucun transfert nécessaire`);
      break;
    }

    // Candidats : donneur = celui qui a le plus de blocs, receveur = le moins
    const donneurs = docBlocs.filter(d => d.blocCount === maxBlocs).sort((a, b) => b.ratio - a.ratio);
    const receveurs = docBlocs.filter(d => d.blocCount === minBlocs).sort((a, b) => a.ratio - b.ratio);

    logs.push(`  │  Déséquilibre : min=${minBlocs} blocs (${receveurs.map(r=>r.key).join(", ")}) vs max=${maxBlocs} blocs (${donneurs.map(d=>d.key).join(", ")})`);

    let bestTransfert = null;
    let bestScore = -Infinity;

    for (const receveur of receveurs) {
      for (const donneur of donneurs) {
        for (const blocIdx of blocsParMedecin[donneur.key]) {
          const bloc = blocs[blocIdx];
          const desidReceveur = desiderataParMedecin[receveur.key];

          // Vérifier la disponibilité du receveur
          if (!estDisponiblePourBloc(desidReceveur, bloc.jours)) {
            logs.push(`  │     ❌ bloc#${blocIdx + 1} ${donneur.key}→${receveur.key} : receveur INDISPONIBLE`);
            continue;
          }

          // Vérifier l'espacement pour le receveur (simuler l'ajout)
          const newIndicesReceveur = [...blocsParMedecin[receveur.key], blocIdx].sort((a, b) => a - b);
          let espReceveurOK = true;
          let espMinReceveur = Infinity;
          for (let k = 1; k < newIndicesReceveur.length; k++) {
            const gap = newIndicesReceveur[k] - newIndicesReceveur[k - 1];
            espMinReceveur = Math.min(espMinReceveur, gap);
            if (gap < ESPACEMENT_MIN) {
              espReceveurOK = false;
              break;
            }
          }
          if (!espReceveurOK) {
            logs.push(`  │     ❌ bloc#${blocIdx + 1} ${donneur.key}→${receveur.key} : espacement receveur INSUFFISANT (esp=${espMinReceveur}<${ESPACEMENT_MIN})`);
            continue;
          }

          // Calculer le gain en écart de ratios
          const tailleBloc = bloc.jours.length;
          const newCountReceveur = (compteurs[receveur.key] || 0) + tailleBloc;
          const newCountDonneur = (compteurs[donneur.key] || 0) - tailleBloc;

          // Phase 1 transferts : sans ajustement (équité brute basée sur quotité)
          const newRatios = interieursEquite.map(mm => {
            const kk = medKey(mm.nom, mm.prenom);
            if (kk === receveur.key) return newCountReceveur / (mm.quotite / 100);
            if (kk === donneur.key) return newCountDonneur / (mm.quotite / 100);
            return ((compteurs[kk] || 0)) / (mm.quotite / 100);
          });
          const newEcart = Math.max(...newRatios) - Math.min(...newRatios);
          const ecartActuel = computeEcart();
          const gainEcart = ecartActuel - newEcart;

          // Score de préférence du receveur (souhaite=1 > dispo=2)
          const prefScore = scorePrefBloc(desidReceveur, bloc.jours);
          const prefBonus = prefScore === 0 ? 2 : prefScore <= 6 ? 1 : 0; // bonus si souhaite absolument
          const score = gainEcart + prefBonus + (espMinReceveur / 100); // préférer meilleur espacement

          logs.push(`  │     ✅ bloc#${blocIdx + 1} ${donneur.key}→${receveur.key} : POSSIBLE (gain écart=${gainEcart.toFixed(1)}, pref=${prefScore === 0 ? "⭐souhaite" : "dispo"}, esp=${espMinReceveur === Infinity ? "1er" : espMinReceveur}) score=${score.toFixed(2)}`);

          if (score > bestScore) {
            bestScore = score;
            bestTransfert = { blocIdx, donneur: donneur.key, receveur: receveur.key, tailleBloc, gainEcart, prefScore, espMinReceveur };
          }
        }
      }
    }

    if (!bestTransfert) {
      if (transfertsEffectues === 0) {
        logs.push(`  │  ⚠️ Aucun transfert possible (contraintes de disponibilité/espacement)`);
      }
      break;
    }

    // Exécuter le transfert
    const { blocIdx, donneur, receveur, tailleBloc, gainEcart, prefScore } = bestTransfert;
    const bloc = blocs[blocIdx];

    // Retirer du donneur
    for (const jour of bloc.jours) {
      affectationsBlocs[jour] = receveur;
      const moisDuJour = jour.substring(0, 7);
      if (compteursParMois[donneur]?.[moisDuJour] !== undefined) compteursParMois[donneur][moisDuJour]--;
      if (compteursParMois[receveur]?.[moisDuJour] !== undefined) compteursParMois[receveur][moisDuJour]++;
    }
    compteurs[donneur] = (compteurs[donneur] || 0) - tailleBloc;
    compteurs[receveur] = (compteurs[receveur] || 0) + tailleBloc;
    blocsParMedecin[donneur] = blocsParMedecin[donneur].filter(idx => idx !== blocIdx);
    blocsParMedecin[receveur] = [...blocsParMedecin[receveur], blocIdx].sort((a, b) => a - b);
    affectationParBloc[blocIdx] = receveur;

    transfertsEffectues++;
    const joursList = bloc.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
    const prefLabel = prefScore === 0 ? "⭐souhaite" : "disponible";
    logs.push(`  │`);
    logs.push(`  │  🔀 TRANSFERT #${transfertsEffectues} : bloc#${blocIdx + 1} [${tailleBloc}j] ${joursList}`);
    logs.push(`  │     ${donneur} (${blocsParMedecin[donneur].length + 1}→${blocsParMedecin[donneur].length} blocs) → ${receveur} (${blocsParMedecin[receveur].length - 1}→${blocsParMedecin[receveur].length} blocs)`);
    logs.push(`  │     Préférence receveur : ${prefLabel} | Gain écart : ${gainEcart.toFixed(1)}`);
    transfertContinue = true;
  }

  if (transfertsEffectues > 0) {
    logs.push(`\n  📊 ${transfertsEffectues} transfert(s) effectué(s)`);
    logEquiteTable(logs, "ÉQUITÉ APRÈS TRANSFERTS", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);
  }

  // ═══════════════════════════════════════════════════════════
  // OPTIMISATION PAR REMPLACEMENT
  // Pour chaque bloc non attribué, vérifier si en remplaçant
  // un bloc voisin (qui cause le conflit d'espacement) par ce bloc,
  // le bloc libéré peut être pris par un autre médecin
  // ═══════════════════════════════════════════════════════════
  const blocsNonAttribues = [];
  for (let i = 0; i < blocs.length; i++) {
    if (affectationParBloc[i] === null) blocsNonAttribues.push(i);
  }

  if (blocsNonAttribues.length > 0) {
    logs.push("");
    logs.push("  ┌─────────────────────────────────────────────────────────────");
    logs.push(`  │ Optimisation par REMPLACEMENT (${blocsNonAttribues.length} bloc(s) non attribué(s))`);
    logs.push("  │ Tente de remplacer un bloc voisin pour libérer une place");
    logs.push("  └─────────────────────────────────────────────────────────────");

    let remplacementsEffectues = 0;
    let continueRemplacement = true;

    while (continueRemplacement) {
      continueRemplacement = false;
      let bestRemplacement = null;
      let bestScore = -Infinity;

      // Recalculer les blocs non attribués à chaque itération
      const blocsLibres = [];
      for (let i = 0; i < blocs.length; i++) {
        if (affectationParBloc[i] === null) blocsLibres.push(i);
      }
      if (blocsLibres.length === 0) break;

      for (const blocLibreIdx of blocsLibres) {
        const blocLibre = blocs[blocLibreIdx];

        // Pour chaque médecin : est-il disponible pour ce bloc mais bloqué par l'espacement ?
        for (const m of interieursEquite) {
          const key = medKey(m.nom, m.prenom);
          const desid = desiderataParMedecin[key];
          if (!estDisponiblePourBloc(desid, blocLibre.jours)) continue;

          // Ce médecin est-il bloqué par l'espacement ?
          const espMin = getEspacementMin(key, blocLibreIdx);
          if (espMin >= ESPACEMENT_MIN) continue; // Pas bloqué, c'est un autre problème (plafond ?)

          // Trouver le(s) bloc(s) voisin(s) qui causent le conflit
          const indices = blocsParMedecin[key] || [];
          for (const blocVoisinIdx of indices) {
            const dist = Math.abs(blocLibreIdx - blocVoisinIdx);
            if (dist >= ESPACEMENT_MIN) continue; // Ce bloc n'est pas le problème

            const blocVoisin = blocs[blocVoisinIdx];

            // Simuler le remplacement :
            // 1) Retirer blocVoisin du médecin
            // 2) Attribuer blocLibre au médecin
            // 3) Vérifier que l'espacement du médecin est OK avec le remplacement
            const newIndicesMed = indices.filter(idx => idx !== blocVoisinIdx).concat([blocLibreIdx]).sort((a, b) => a - b);
            let espOK = true;
            for (let k = 1; k < newIndicesMed.length; k++) {
              if (newIndicesMed[k] - newIndicesMed[k - 1] < ESPACEMENT_MIN) {
                espOK = false;
                break;
              }
            }
            if (!espOK) continue;

            // 4) Le bloc libéré (blocVoisin) peut-il être pris par un AUTRE médecin ?
            let meilleurRepreneur = null;
            let meilleurRepreneurRatio = Infinity;
            for (const m2 of interieursEquite) {
              const key2 = medKey(m2.nom, m2.prenom);
              if (key2 === key) continue;
              const desid2 = desiderataParMedecin[key2];
              if (!estDisponiblePourBloc(desid2, blocVoisin.jours)) continue;

              // Vérifier espacement pour le repreneur (en considérant ses blocs actuels)
              const indices2 = blocsParMedecin[key2] || [];
              const newIndices2 = [...indices2, blocVoisinIdx].sort((a, b) => a - b);
              let esp2OK = true;
              for (let k = 1; k < newIndices2.length; k++) {
                if (newIndices2[k] - newIndices2[k - 1] < ESPACEMENT_MIN) {
                  esp2OK = false;
                  break;
                }
              }
              if (!esp2OK) continue;

              // Vérifier que le repreneur n'est pas au plafond
              if (indices2.length >= plafondParMed[key2]) continue;

              const ratio2 = getRatio(key2);
              if (ratio2 < meilleurRepreneurRatio) {
                meilleurRepreneurRatio = ratio2;
                meilleurRepreneur = key2;
              }
            }

            if (!meilleurRepreneur) continue;

            // Calculer le score : on gagne 1 bloc attribué + amélioration de l'écart
            const tailleLibre = blocLibre.jours.length;
            const tailleVoisin = blocVoisin.jours.length;

            // Nouveau compteurs simulés
            const newCountMed = (compteurs[key] || 0) - tailleVoisin + tailleLibre;
            const newCountRepreneur = (compteurs[meilleurRepreneur] || 0) + tailleVoisin;

            // Phase 1 remplacement : sans ajustement (équité brute basée sur quotité)
            const newRatios = interieursEquite.map(mm => {
              const kk = medKey(mm.nom, mm.prenom);
              if (kk === key) return newCountMed / (mm.quotite / 100);
              if (kk === meilleurRepreneur) return newCountRepreneur / (mm.quotite / 100);
              return ((compteurs[kk] || 0)) / (mm.quotite / 100);
            });
            const newEcart = Math.max(...newRatios) - Math.min(...newRatios);
            const ecartActuel = computeEcart();

            // Score : priorité à réduire les blocs non attribués, puis à l'écart
            const score = 100 + (ecartActuel - newEcart);

            if (score > bestScore) {
              bestScore = score;
              bestRemplacement = {
                blocLibreIdx, blocVoisinIdx,
                medecin: key, repreneur: meilleurRepreneur,
                tailleLibre, tailleVoisin, newEcart, ecartActuel
              };
            }
          }
        }
      }

      if (!bestRemplacement) {
        if (remplacementsEffectues === 0) {
          logs.push(`  ℹ️ Aucun remplacement possible (pas de repreneur disponible pour le bloc libéré)`);
        }
        break;
      }

      // Exécuter le remplacement
      const { blocLibreIdx, blocVoisinIdx, medecin, repreneur, tailleLibre, tailleVoisin, newEcart, ecartActuel } = bestRemplacement;
      const blocLibre = blocs[blocLibreIdx];
      const blocVoisin = blocs[blocVoisinIdx];

      // Retirer blocVoisin du médecin
      for (const j of blocVoisin.jours) {
        const moisJ = j.substring(0, 7);
        if (compteursParMois[medecin]?.[moisJ] !== undefined) compteursParMois[medecin][moisJ]--;
      }
      compteurs[medecin] = (compteurs[medecin] || 0) - tailleVoisin;
      blocsParMedecin[medecin] = blocsParMedecin[medecin].filter(idx => idx !== blocVoisinIdx);
      affectationParBloc[blocVoisinIdx] = null;
      for (const j of blocVoisin.jours) affectationsBlocs[j] = null;

      // Attribuer blocLibre au médecin
      attribuerBloc(blocLibreIdx, medecin);

      // Attribuer blocVoisin au repreneur
      attribuerBloc(blocVoisinIdx, repreneur);

      remplacementsEffectues++;
      const joursLibre = blocLibre.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
      const joursVoisin = blocVoisin.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
      logs.push(`  🔄 REMPLACEMENT #${remplacementsEffectues} :`);
      logs.push(`  │  ${medecin} : lâche bloc#${blocVoisinIdx + 1} [${joursVoisin}] → prend bloc#${blocLibreIdx + 1} [${joursLibre}]`);
      logs.push(`  │  ${repreneur} : récupère bloc#${blocVoisinIdx + 1} [${joursVoisin}]`);
      logs.push(`  │  Écart : ${ecartActuel.toFixed(1)} → ${newEcart.toFixed(1)}`);

      continueRemplacement = true;
    }

    if (remplacementsEffectues > 0) {
      logs.push(`\n  📊 ${remplacementsEffectues} remplacement(s) effectué(s)`);
      logEquiteTable(logs, "ÉQUITÉ APRÈS REMPLACEMENTS", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);
    }
  }

  // Blocs toujours non attribués après optimisation complète
  const blocsEncoreLibres = [];
  for (let i = 0; i < blocs.length; i++) {
    if (affectationParBloc[i] === null) blocsEncoreLibres.push(i);
  }
  if (blocsEncoreLibres.length > 0) {
    logs.push(`\n  ⚠️ ${blocsEncoreLibres.length} bloc(s) toujours non attribué(s) après toutes les optimisations :`);
    for (const idx of blocsEncoreLibres) {
      const b = blocs[idx];
      const joursList = b.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", ");
      logs.push(`  │  🚫 Bloc #${idx + 1} [${b.jours.length}j] : ${joursList} → attribution manuelle`);
    }
  } else {
    logs.push(`\n  ✅ Tous les ${blocs.length} blocs ont été attribués !`);
  }

  // Log espacement final
  logs.push("\n  ┌─── ESPACEMENT FINAL PAR MÉDECIN ───");
  let totalConsec = 0;
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    const indices = blocsParMedecin[key] || [];
    if (indices.length === 0) {
      logs.push(`  │  ${key.padEnd(25)} : aucun bloc`);
      continue;
    }
    const espacements = [];
    for (let i = 1; i < indices.length; i++) espacements.push(indices[i] - indices[i - 1]);
    const blocsStr = indices.map(i => `#${i + 1}`).join(", ");
    const espStr = espacements.length > 0 ? ` — écarts: ${espacements.join(", ")}` : "";
    const hasConsec = espacements.some(e => e < ESPACEMENT_MIN);
    if (hasConsec) totalConsec++;
    logs.push(`  │  ${key.padEnd(25)} : ${indices.length} blocs [${blocsStr}]${espStr}${hasConsec ? ` ⚠️ESP<${ESPACEMENT_MIN}` : " ✅"}`);
  }
  logs.push("  └──────────────────────────────────");

  // Propositions pour le frontend
  const propositions = blocs.map((bloc, origIdx) => {
    const candidats = [];
    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      if (!estDisponiblePourBloc(desid, bloc.jours)) continue;
      const score = scorePrefBloc(desid, bloc.jours);
      const nbActuel = compteurs[key] || 0;
      const ajust = ajustementsEffectifs[key] || 0;
      const nbPourRatio = key === affectationParBloc[origIdx] ? nbActuel - bloc.jours.length : nbActuel;
      // Phase 1 remplacement : sans ajustement (équité brute basée sur quotité)
      const ratioEquite = nbPourRatio / (m.quotite / 100);
      const prefDetail = bloc.jours.map(j => `${getDayNameShort(j)}:${getPref(desid, j)}`).join(" ");
      candidats.push({ key, score, nbActuel: nbPourRatio, quotite: m.quotite, ratioEquite, prefDetail, nom: m.nom, prenom: m.prenom, ajust });
    }
    candidats.sort((a, b) => {
      if (a.ratioEquite !== b.ratioEquite) return a.ratioEquite - b.ratioEquite;
      if (a.score !== b.score) return a.score - b.score;
      return a.key.localeCompare(b.key);
    });
    // Liste complète de tous les médecins (intérieurs + extérieurs) pour attribution manuelle
    const tousCandidats = [];
    const tousMedecins = [...interieursEquite, ...exterieurs];
    for (const m of tousMedecins) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      const disponible = desid ? estDisponiblePourBloc(desid, bloc.jours) : false;
      const score = desid ? scorePrefBloc(desid, bloc.jours) : 999;
      const prefLabel = !desid ? "sans desiderata" : !disponible ? "indisponible" : score === 0 ? "souhaite absolument" : "disponible";
      tousCandidats.push({
        key, nom: m.nom, prenom: m.prenom, quotite: m.quotite,
        origine: m.origine || "interieur", pref: prefLabel, disponible,
      });
    }
    // Trier : disponibles en premier, puis par origine (intérieur d'abord), puis par nom
    tousCandidats.sort((a, b) => {
      if (a.disponible !== b.disponible) return a.disponible ? -1 : 1;
      if (a.origine !== b.origine) return a.origine === "interieur" ? -1 : 1;
      return a.key.localeCompare(b.key);
    });

    const choisiKey = affectationParBloc[origIdx];
    const choisiIndex = candidats.findIndex(c => c.key === choisiKey);

    // Diagnostic pour blocs non attribués
    let diagnostic = null;
    if (!choisiKey) {
      diagnostic = { bloqueParEspacement: [], indisponibles: [], propositions: [] };

      for (const m of interieursEquite) {
        const key = medKey(m.nom, m.prenom);
        const desid = desiderataParMedecin[key];
        const disponible = estDisponiblePourBloc(desid, bloc.jours);

        if (!disponible) {
          // Médecin indisponible — détail par jour
          const joursIndispo = bloc.jours.filter(j => getPref(desid, j) === 0);
          diagnostic.indisponibles.push({
            key, nom: m.nom, prenom: m.prenom,
            joursIndispo: joursIndispo.map(j => `${j}(${getDayNameShort(j)})`),
          });
        } else {
          // Médecin disponible mais bloqué par espacement ?
          const espMin = getEspacementMin(key, origIdx);
          if (espMin < ESPACEMENT_MIN) {
            // Trouver le bloc voisin qui cause le conflit
            const indices = blocsParMedecin[key] || [];
            const blocsVoisins = indices
              .filter(idx => Math.abs(idx - origIdx) < ESPACEMENT_MIN)
              .map(idx => {
                const bv = blocs[idx];
                return {
                  blocNum: idx + 1,
                  jours: bv.jours.map(j => `${j}(${getDayNameShort(j)})`).join(", "),
                  distance: Math.abs(idx - origIdx),
                };
              });
            diagnostic.bloqueParEspacement.push({
              key, nom: m.nom, prenom: m.prenom,
              espacement: espMin,
              blocsVoisins,
            });
          }
          // Sinon, médecin est au plafond → il est dans les candidats normaux
        }
      }

      // Proposition A : Accepter 2 weekends consécutifs (relâcher l'espacement)
      if (diagnostic.bloqueParEspacement.length > 0) {
        for (const bloque of diagnostic.bloqueParEspacement) {
          diagnostic.propositions.push({
            type: "A",
            label: `Demander à ${bloque.nom} ${bloque.prenom} d'accepter 2 weekends rapprochés`,
            detail: `Bloqué par le bloc #${bloque.blocsVoisins[0]?.blocNum} (espacement=${bloque.espacement}, minimum requis=${ESPACEMENT_MIN})`,
            medecin: bloque.key,
          });
        }
      }

      // Proposition B : Demander aux médecins intérieurs indisponibles de modifier leurs desiderata
      if (diagnostic.indisponibles.length > 0) {
        diagnostic.propositions.push({
          type: "B",
          label: `Demander aux médecins intérieurs indisponibles de modifier leurs desiderata pour ce weekend`,
          detail: "",
          medecins: diagnostic.indisponibles.map(ind => ({ nom: ind.nom, prenom: ind.prenom, key: ind.key, joursIndispo: ind.joursIndispo })),
        });
      }

      // Proposition C : Faire appel à un médecin extérieur disponible (exception)
      diagnostic.exterieursDisponibles = [];
      if (exterieurs && exterieurs.length > 0) {
        for (const mExt of exterieurs) {
          const keyExt = medKey(mExt.nom, mExt.prenom);
          const desidExt = desiderataParMedecin[keyExt];
          if (!desidExt) continue; // Pas de desiderata soumis
          if (!estDisponiblePourBloc(desidExt, bloc.jours)) continue;
          const scoreExt = scorePrefBloc(desidExt, bloc.jours);
          const prefLabel = scoreExt === 0 ? "souhaite absolument" : "disponible";
          diagnostic.exterieursDisponibles.push({
            key: keyExt, nom: mExt.nom, prenom: mExt.prenom,
            quotite: mExt.quotite, pref: prefLabel,
          });
        }
      }
      if (diagnostic.exterieursDisponibles.length > 0) {
        diagnostic.propositions.push({
          type: "C",
          label: `Faire appel à un médecin extérieur disponible ce weekend (exception)`,
          detail: "",
          medecins: diagnostic.exterieursDisponibles,
        });
      }
    }

    return { bloc, candidats, tousCandidats, choisiKey, choisiIndex: choisiIndex >= 0 ? choisiIndex : 0, diagnostic };
  });

  // Tableau d'équité finale
  logs.push("");
  logEquiteTable(logs, "ÉQUITÉ FINALE PHASE 1", interieursEquite, compteurs, ajustementsEffectifs, blocsParMedecin);
  const ratiosFinaux = interieursEquite.map(m => getRatio(medKey(m.nom, m.prenom)));
  const ecartFinal = Math.max(...ratiosFinaux) - Math.min(...ratiosFinaux);
  logs.push(`  📊 Écart final ratios : ${ecartFinal.toFixed(2)}, espacements insuffisants : ${totalConsec === 0 ? "✅ aucun" : `⚠️ ${totalConsec} médecin(s)`}`);

  return {
    affectationsBlocs,
    propositions,
    compteurs: { ...compteurs },
    compteursParMois: JSON.parse(JSON.stringify(compteursParMois)),
    logs,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 2 : RÈGLE 5 — Attribution Nguyen (1 jeudi/mois)
// ═══════════════════════════════════════════════════════════════════════

export function phase2_attribuerNguyen({ prepData, affectationsValidees, compteurs, compteursParMois }) {
  const { nguyenMedKey, nguyenMed, desiderataParMedecin, joursEnBloc } = prepData;
  const moisList = prepData.tousLesJours.length > 0
    ? [...new Set(prepData.tousLesJours.map(j => j.substring(0, 7)))]
    : [];

  const logs = [];
  const affectationsNguyen = {};
  const propositionsNguyen = [];

  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  📌 PHASE 2 / RÈGLE 5 : Attribution Dr Nguyen Yên-lan (1 jeudi/mois)");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  if (!nguyenMedKey) {
    logs.push("  ⚠️ Dr Nguyen non trouvé(e) dans la liste des intérieurs");
    return { affectationsNguyen, propositionsNguyen, compteurs: { ...compteurs }, compteursParMois: JSON.parse(JSON.stringify(compteursParMois)), logs };
  }

  for (const mois of moisList) {
    const [y, mo] = mois.split("-").map(Number);
    const nbJours = new Date(y, mo, 0).getDate();
    const jeudisDispos = [];

    logs.push(`\n  ┌─── ${mois} : Recherche des jeudis disponibles ───`);

    for (let d = 1; d <= nbJours; d++) {
      const ds = dateStr(y, mo, d);
      const dow = getDayOfWeek(ds);
      if (dow === 4) {
        const enBloc = joursEnBloc.has(ds);
        const dejaAffecte = !!affectationsValidees[ds];
        const pref = getPref(desiderataParMedecin[nguyenMedKey], ds);

        if (enBloc) {
          logs.push(`  │  ${ds} (jeu) — dans un bloc → exclu`);
        } else if (dejaAffecte) {
          logs.push(`  │  ${ds} (jeu) — déjà affecté (${affectationsValidees[ds]}) → exclu`);
        } else if (pref === 0) {
          logs.push(`  │  ${ds} (jeu) — ${prefLabel(pref)} → exclu`);
        } else {
          logs.push(`  │  ${ds} (jeu) — ${prefLabel(pref)} → ÉLIGIBLE`);
          jeudisDispos.push({ date: ds, pref });
        }
      }
    }

    if (jeudisDispos.length === 0) {
      logs.push(`  │  ⚠️ AUCUN jeudi disponible pour ${mois} — pas d'attribution forcée`);
      propositionsNguyen.push({ mois, jeudisDispos: [], choisiDate: null, choisiIndex: -1 });
      logs.push(`  └──────────────────────────────────`);
      continue;
    }

    jeudisDispos.sort((a, b) => a.pref - b.pref);
    const choisi = jeudisDispos[0];
    affectationsNguyen[choisi.date] = nguyenMedKey;
    compteurs[nguyenMedKey] = (compteurs[nguyenMedKey] || 0) + 1;
    if (compteursParMois[nguyenMedKey]?.[mois] !== undefined) {
      compteursParMois[nguyenMedKey][mois]++;
    }

    propositionsNguyen.push({ mois, jeudisDispos, choisiDate: choisi.date, choisiIndex: 0 });

    logs.push(`  │  ✅ PROPOSITION : ${choisi.date} (${prefLabel(choisi.pref)})`);
    logs.push(`  └──────────────────────────────────`);
  }

  logs.push(`\n  📊 Nguyen : ${compteurs[nguyenMedKey] || 0} astreinte(s) proposée(s)`);

  return {
    affectationsNguyen,
    propositionsNguyen,
    compteurs: { ...compteurs },
    compteursParMois: JSON.parse(JSON.stringify(compteursParMois)),
    logs,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3 : Jours restants
// Étape 3a : 1 astreinte/mois par médecin (tous, à plat sans Phase 1)
// Étape 3b : Complétion par les intérieurs (charge totale)
// ═══════════════════════════════════════════════════════════════════════

export function phase3_joursRestants({
  prepData, affectationsValidees, compteurs, compteursParMois, ajustements = {},
}) {
  const {
    exterieurs, interieursEquite, desiderataParMedecin,
    tousLesJours, joursEnBloc,
  } = prepData;
  const moisList = [...new Set(tousLesJours.map(j => j.substring(0, 7)))];

  const logs = [];
  const affectationsPhase3 = {};

  const ajustementsEffectifs = {};
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    ajustementsEffectifs[key] = ajustements[key] || 0;
  }

  // Tous les médecins (intérieurs + extérieurs + Nguyen) pour l'étape 2a
  // Nguyen est exclue de interieursEquite mais doit participer à l'attribution des jours
  const nguyenMed = prepData.nguyenMed;
  const tousMedecins = nguyenMed
    ? [...interieursEquite, nguyenMed, ...exterieurs]
    : [...interieursEquite, ...exterieurs];

  // Tracker espacement inter-astreinte en jours calendaires
  const joursAttribuesParMed = {};
  for (const m of tousMedecins) {
    joursAttribuesParMed[medKey(m.nom, m.prenom)] = [];
  }
  // Pré-remplir avec les astreintes Phase 1 + 2
  for (const [jour, medKeyVal] of Object.entries(affectationsValidees)) {
    if (medKeyVal && joursAttribuesParMed[medKeyVal]) {
      joursAttribuesParMed[medKeyVal].push(jour);
    }
  }

  const getEspacementInterAstreinte = (medKeyStr, jour) => {
    const jours = joursAttribuesParMed[medKeyStr] || [];
    if (jours.length === 0) return 999;
    const jourDate = parseDate(jour).getTime();
    let minDist = 999;
    for (const j of jours) {
      const dist = Math.abs(jourDate - parseDate(j).getTime()) / (1000 * 60 * 60 * 24);
      minDist = Math.min(minDist, dist);
    }
    return minDist;
  };

  // ═══ Nguyen : contrainte 1 jeudi/mois intégrée ═══
  const { nguyenMedKey } = prepData;

  // ═══ ÉTAPE 2a : 1 astreinte/mois par médecin (tous, à plat) ═══
  // Pour Nguyen Yên-lan : uniquement les jeudis (Règle 5)
  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  📌 ÉTAPE 2a : 1 astreinte/mois par médecin (intérieurs + extérieurs)");
  logs.push("  📌 Traitement à plat — sans tenir compte de la charge Phase 1");
  if (nguyenMedKey) {
    logs.push(`  📌 Contrainte Nguyen (${nguyenMedKey}) : uniquement les jeudis`);
  }
  logs.push("═══════════════════════════════════════════════════════════════════════");

  let nb2a = 0;
  for (const mois of moisList) {
    logs.push(`\n  ┌─── ${mois} ───`);

    const [y, mo] = mois.split("-").map(Number);
    const nbJoursMois = new Date(y, mo, 0).getDate();

    // Pré-calculer le nombre de jours disponibles par médecin pour ce mois
    // afin de trier les médecins les plus contraints d'abord
    const medsAvecDispos = [];
    for (const med of tousMedecins) {
      const key = medKey(med.nom, med.prenom);
      const desid = desiderataParMedecin[key];
      const isNguyen = key === nguyenMedKey;

      // Déjà couvert ce mois
      if ((compteursParMois[key]?.[mois] || 0) >= 1) {
        logs.push(`  │  ${key} — déjà couvert ce mois → skip`);
        continue;
      }
      if (!desid) {
        logs.push(`  │  ${key} — aucun desiderata → skip`);
        continue;
      }

      // Compter les jours disponibles hors bloc ce mois
      let nbDispos = 0;
      for (let d = 1; d <= nbJoursMois; d++) {
        const ds = dateStr(y, mo, d);
        if (affectationsValidees[ds] || affectationsPhase3[ds]) continue;
        if (joursEnBloc.has(ds)) continue;
        if (isNguyen && getDayOfWeek(ds) !== 4) continue;
        const pref = getPref(desid, ds);
        if (pref !== 0) nbDispos++;
      }

      medsAvecDispos.push({ med, key, desid, isNguyen, nbDispos });
    }

    // Trier : les plus contraints d'abord (moins de jours disponibles)
    medsAvecDispos.sort((a, b) => a.nbDispos - b.nbDispos);

    for (const { med, key, desid, isNguyen, nbDispos } of medsAvecDispos) {
      const joursDispos = [];

      for (let d = 1; d <= nbJoursMois; d++) {
        const ds = dateStr(y, mo, d);
        if (affectationsValidees[ds] || affectationsPhase3[ds]) continue;
        if (joursEnBloc.has(ds)) continue;

        // Contrainte Nguyen : jeudis uniquement
        if (isNguyen) {
          const dow = getDayOfWeek(ds);
          if (dow !== 4) continue; // 4 = jeudi
        }

        const pref = getPref(desid, ds);
        if (pref === 0) continue;

        const espInter = getEspacementInterAstreinte(key, ds);
        const sP = scorePrefJour(desid, ds);
        joursDispos.push({ date: ds, scorePref: sP, espInter, pref });
      }

      if (joursDispos.length === 0) {
        if (isNguyen) {
          logs.push(`  │  ${key} — aucun jeudi dispo ce mois → ⚠️ non servi (contrainte jeudi)`);
        } else {
          logs.push(`  │  ${key} — aucun jour dispo ce mois → ⚠️ non servi`);
        }
        continue;
      }

      // Trier : souhaite > dispo, puis espacement inter-astreinte max
      joursDispos.sort((a, b) => {
        if (a.scorePref !== b.scorePref) return a.scorePref - b.scorePref;
        return b.espInter - a.espInter;
      });

      const choisi = joursDispos[0];
      affectationsPhase3[choisi.date] = key;
      compteurs[key] = (compteurs[key] || 0) + 1;
      if (compteursParMois[key]?.[mois] !== undefined) compteursParMois[key][mois]++;
      joursAttribuesParMed[key].push(choisi.date);
      nb2a++;

      const espLabel = choisi.espInter === 999 ? "1ère astr." : `esp=${Math.round(choisi.espInter)}j`;
      const nguyenTag = isNguyen ? " [JEUDI]" : "";
      logs.push(`  │  ${key} → ${choisi.date} (${getDayName(choisi.date)}) — ${prefLabel(choisi.pref)} (${espLabel})${nguyenTag} [${joursDispos.length} possibles, ${nbDispos} dispos mois]`);
    }
    logs.push(`  └──────────────────────────────────`);
  }

  logs.push(`\n  📊 Étape 2a : ${nb2a} astreinte(s) attribuée(s) (1/mois/médecin)`);

  // ═══ ÉTAPE 2b : Complétion par les intérieurs ═══
  logs.push("");
  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  📌 ÉTAPE 2b : Complétion jours restants → intérieurs uniquement");
  logs.push("  📌 Charge totale (Phase 1 + 2a + 2b) prise en compte pour l'équité");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  const hasAjust = Object.values(ajustementsEffectifs).some(v => v !== 0);
  if (hasAjust) {
    logs.push("  ┌─── AJUSTEMENTS BONUS/MALUS ───");
    for (const [key, val] of Object.entries(ajustementsEffectifs)) {
      if (val !== 0) logs.push(`  │  ${key} : ${val > 0 ? "+" : ""}${val}`);
    }
    logs.push("  └──────────────────────────────────");
  }

  const joursResteApres3a = tousLesJours.filter((j) =>
    !affectationsValidees[j] && !affectationsPhase3[j]
  );
  logs.push(`\n  📊 ${joursResteApres3a.length} jours restants pour les intérieurs (après étape 2a)`);

  let currentMois = "";
  let jourDansEtape = 0;
  for (const jour of joursResteApres3a) {
    jourDansEtape++;
    const moisDuJour = jour.substring(0, 7);

    if (moisDuJour !== currentMois) {
      if (currentMois !== "") {
        logs.push(`  └──────────────────────────────────`);
        logEquiteTable(logs, `ÉQUITÉ FIN ${currentMois}`, interieursEquite, compteurs, ajustementsEffectifs);
      }
      currentMois = moisDuJour;
      logs.push(`\n  ┌─── ${moisDuJour} ───`);
    }

    const candidats = [];
    const rejetes = [];

    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      const desid = desiderataParMedecin[key];
      const pref = getPref(desid, jour);
      if (pref === 0) { rejetes.push(key); continue; }

      const nbActuel = compteurs[key] || 0;
      const ajust = ajustementsEffectifs[key] || 0;
      // ✅ Ajust positif = besoin de PLUS de jours → ratio PLUS bas → sélectionné PLUS souvent
      // ✅ Ajust négatif = besoin de MOINS de jours → ratio PLUS élevé → sélectionné MOINS souvent
      const ratioEquite = (nbActuel - ajust) / (m.quotite / 100);
      const sP = scorePrefJour(desid, jour);
      const espInter = getEspacementInterAstreinte(key, jour);

      candidats.push({ key, ratioEquite, scorePref: sP, nbActuel, quotite: m.quotite, pref, espInter, ajust });
    }

    if (candidats.length === 0) {
      logs.push(`  │  ${jour} (${getDayNameShort(jour)}) — ⚠️ AUCUN candidat [rej: ${rejetes.join(", ")}]`);
      affectationsPhase3[jour] = null;
      continue;
    }

    candidats.sort((a, b) => {
      if (a.ratioEquite !== b.ratioEquite) return a.ratioEquite - b.ratioEquite;
      if (a.scorePref !== b.scorePref) return a.scorePref - b.scorePref;
      return b.espInter - a.espInter;
    });

    const choisi = candidats[0];
    affectationsPhase3[jour] = choisi.key;
    compteurs[choisi.key] = (compteurs[choisi.key] || 0) + 1;
    if (compteursParMois[choisi.key]?.[moisDuJour] !== undefined) {
      compteursParMois[choisi.key][moisDuJour]++;
    }
    joursAttribuesParMed[choisi.key].push(jour);

    const nbRejet = rejetes.length;
    const espLabel = choisi.espInter === 999 ? "" : ` esp=${Math.round(choisi.espInter)}j`;
    logs.push(`  │  ${jour} (${getDayNameShort(jour)}) → ${choisi.key} [${choisi.nbActuel + 1} astr, r=${choisi.ratioEquite.toFixed(2)}${espLabel}] — ${candidats.length} cand.${nbRejet > 0 ? `, ${nbRejet} rej.` : ""}`);

    if (jourDansEtape % 20 === 0) {
      logs.push(`  │`);
      logEquiteTable(logs, `ÉQUITÉ INTERMÉDIAIRE (${jourDansEtape}j)`, interieursEquite, compteurs, ajustementsEffectifs);
    }
  }

  if (currentMois !== "") {
    logs.push(`  └──────────────────────────────────`);
    logEquiteTable(logs, `ÉQUITÉ FINALE`, interieursEquite, compteurs, ajustementsEffectifs);
  }

  // ═══ STATISTIQUES FINALES ═══
  logs.push("");
  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  📊 STATISTIQUES FINALES (PHASE 2)");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  const toutesAffectations = { ...affectationsValidees, ...affectationsPhase3 };
  const totalAttribue = Object.values(toutesAffectations).filter(Boolean).length;
  const totalNonAttribue = Object.values(toutesAffectations).filter((v) => v === null).length;

  logs.push(`\n  📊 BILAN : ${totalAttribue} attribués, ${totalNonAttribue} non attribués, ${tousLesJours.length} jours total`);

  logs.push("\n  ┌─── VÉRIFICATION ÉQUITÉ ───");
  const ratios = interieursEquite.map((m) => {
    const key = medKey(m.nom, m.prenom);
    const nb = compteurs[key] || 0;
    return { key, nb, ratio: nb / (m.quotite / 100), quotite: m.quotite };
  });
  ratios.sort((a, b) => a.ratio - b.ratio);
  const ecart = ratios.length > 1 ? ratios[ratios.length - 1].ratio - ratios[0].ratio : 0;
  for (const r of ratios) {
    logs.push(`  │  ${r.key.padEnd(25)} ${String(r.nb).padStart(3)} astr. / ${r.quotite}% = ratio ${r.ratio.toFixed(2)}`);
  }
  logs.push(`  │  Écart max : ${ecart.toFixed(2)} ${ecart <= 2 ? "✅" : "⚠️"}`);
  logs.push("  └──────────────────────────────────");

  logs.push("\n═══════════════════════════════════════════════════════════════════════");
  logs.push("  ✅ FIN — RÉSULTAT COMPLET DES 2 PHASES");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  return {
    affectationsPhase3,
    toutesAffectations,
    compteurs: { ...compteurs },
    compteursParMois: JSON.parse(JSON.stringify(compteursParMois)),
    totalAttribue,
    totalNonAttribue,
    logs,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 4 : Rééquilibrage par swaps — convergence ajustements vers 0
// ═══════════════════════════════════════════════════════════════════════

export function phase4_reequilibrage({
  prepData, toutesAffectations, compteurs, compteursParMois, ajustements = {},
}) {
  const {
    interieursEquite, desiderataParMedecin, joursEnBloc, nguyenMedKey,
  } = prepData;

  const logs = [];
  const affectations = { ...toutesAffectations };
  const cpt = { ...compteurs };
  const cptParMois = JSON.parse(JSON.stringify(compteursParMois));

  const ajustementsEffectifs = {};
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    ajustementsEffectifs[key] = ajustements[key] || 0;
  }

  logs.push("═══════════════════════════════════════════════════════════════════════");
  logs.push("  📌 PHASE 3 : Rééquilibrage par swaps");
  logs.push("  📌 Objectif : rapprocher les ajustements finaux de 0 (±1 max)");
  logs.push("═══════════════════════════════════════════════════════════════════════");

  const totalQuotite = interieursEquite.reduce((sum, m) => sum + m.quotite, 0);
  const quotiteMap = {};
  for (const m of interieursEquite) {
    quotiteMap[medKey(m.nom, m.prenom)] = m.quotite;
  }

  // Calcul ajustement final pour un médecin
  const calcAjFinal = (key) => {
    const totalJoursInt = interieursEquite.reduce((s, m) => s + (cpt[medKey(m.nom, m.prenom)] || 0), 0);
    const partEquitable = totalQuotite > 0 ? ((quotiteMap[key] || 100) / totalQuotite) * totalJoursInt : 0;
    const ecart = Math.round((cpt[key] || 0) - partEquitable);
    return (ajustementsEffectifs[key] || 0) - ecart;
  };

  // Identifier les jours échangeables : hors blocs, attribués à un intérieur (pas Nguyen, pas extérieurs)
  const interieursKeys = new Set(interieursEquite.map(m => medKey(m.nom, m.prenom)));

  // Tracker des jours par médecin (pour vérifier l'espacement)
  const joursParMed = {};
  for (const m of interieursEquite) {
    joursParMed[medKey(m.nom, m.prenom)] = [];
  }
  for (const [jour, medK] of Object.entries(affectations)) {
    if (medK && joursParMed[medK]) {
      joursParMed[medK].push(jour);
    }
  }

  // Vérifie que ajouter 'jour' au médecin 'key' ne crée pas de jours consécutifs
  const checkPasConsecutif = (key, jour) => {
    const jours = joursParMed[key] || [];
    const jourMs = parseDate(jour).getTime();
    const unJour = 24 * 60 * 60 * 1000;
    for (const j of jours) {
      const diff = Math.abs(jourMs - parseDate(j).getTime());
      if (diff <= unJour) return false; // jour consécutif ou même jour
    }
    return true;
  };

  const swappableDays = Object.keys(affectations).filter(jour => {
    const medK = affectations[jour];
    return medK && interieursKeys.has(medK) && !joursEnBloc.has(jour);
  });

  logs.push(`  📊 ${swappableDays.length} jours échangeables (hors blocs, intérieurs uniquement)`);

  // Log état initial
  logs.push("\n  ┌─── ÉTAT AVANT RÉÉQUILIBRAGE ───");
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    const aj = calcAjFinal(key);
    logs.push(`  │  ${key.padEnd(25)} ${String(cpt[key] || 0).padStart(3)} jours  aj.final=${aj > 0 ? "+" : ""}${aj}`);
  }
  logs.push("  └──────────────────────────────────");

  let nbSwaps = 0;
  const MAX_ITERATIONS = 100;

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    // Recalculer ajustements finaux actuels
    const ajFinaux = {};
    for (const m of interieursEquite) {
      const key = medKey(m.nom, m.prenom);
      ajFinaux[key] = calcAjFinal(key);
    }

    // Vérifier si tout le monde est dans [-1, +1]
    const maxAbs = Math.max(...Object.values(ajFinaux).map(Math.abs));
    if (maxAbs <= 1) {
      logs.push(`\n  ✅ Tous les ajustements sont dans [-1, +1] après ${nbSwaps} swap(s)`);
      break;
    }

    // Givers : ajust final < 0 (excédentaires), triés du plus négatif au moins négatif
    const givers = Object.entries(ajFinaux)
      .filter(([, v]) => v < -1 || (v < 0))
      .sort((a, b) => a[1] - b[1]);

    // Receivers : ajust final > 0 (déficitaires), triés du plus positif au moins positif
    const receivers = Object.entries(ajFinaux)
      .filter(([, v]) => v > 1 || (v > 0))
      .sort((a, b) => b[1] - a[1]);

    if (givers.length === 0 || receivers.length === 0) {
      logs.push(`\n  ℹ️ Aucune paire giver/receiver disponible après ${nbSwaps} swap(s)`);
      break;
    }

    let bestSwap = null;

    for (const [receiverKey, recvAj] of receivers) {
      const desidReceiver = desiderataParMedecin[receiverKey];

      for (const [giverKey, giveAj] of givers) {

        // Jours du giver qui sont échangeables
        const giverSwappable = swappableDays.filter(j => affectations[j] === giverKey);

        for (const jour of giverSwappable) {
          // Le receiver doit être disponible ce jour
          const pref = getPref(desidReceiver, jour);
          if (pref === 0) continue;

          // Le receiver ne doit pas avoir de jour consécutif
          if (!checkPasConsecutif(receiverKey, jour)) continue;

          // Vérifier que le swap améliore la somme des |ajust|
          const avantSum = Math.abs(giveAj) + Math.abs(recvAj);
          const apresSum = Math.abs(giveAj + 1) + Math.abs(recvAj - 1);
          if (apresSum >= avantSum) continue; // pas d'amélioration

          bestSwap = { jour, giverKey, receiverKey, giveAj, recvAj };
          break;
        }
        if (bestSwap) break;
      }
      if (bestSwap) break;
    }

    if (!bestSwap) {
      logs.push(`\n  ℹ️ Aucun swap valide trouvé après ${nbSwaps} swap(s) (contraintes disponibilité/espacement)`);
      break;
    }

    // Exécuter le swap
    const { jour, giverKey, receiverKey, giveAj, recvAj } = bestSwap;
    affectations[jour] = receiverKey;
    cpt[giverKey] = (cpt[giverKey] || 0) - 1;
    cpt[receiverKey] = (cpt[receiverKey] || 0) + 1;
    const moisDuJour = jour.substring(0, 7);
    if (cptParMois[giverKey]?.[moisDuJour] !== undefined) cptParMois[giverKey][moisDuJour]--;
    if (cptParMois[receiverKey]?.[moisDuJour] !== undefined) cptParMois[receiverKey][moisDuJour]++;

    // Mettre à jour les trackers
    joursParMed[giverKey] = (joursParMed[giverKey] || []).filter(j => j !== jour);
    joursParMed[receiverKey] = [...(joursParMed[receiverKey] || []), jour];

    nbSwaps++;
    logs.push(`  🔄 Swap #${nbSwaps}: ${jour} (${getDayNameShort(jour)}) : ${giverKey} → ${receiverKey}  [aj: ${giveAj > 0 ? "+" : ""}${giveAj}→${giveAj + 1 > 0 ? "+" : ""}${giveAj + 1} / ${recvAj > 0 ? "+" : ""}${recvAj}→${recvAj - 1 > 0 ? "+" : ""}${recvAj - 1}]`);
  }

  // Bilan final
  logs.push(`\n  📊 BILAN Phase 3 : ${nbSwaps} swap(s) effectué(s)`);
  logs.push("  ┌─── AJUSTEMENTS APRÈS RÉÉQUILIBRAGE ───");
  for (const m of interieursEquite) {
    const key = medKey(m.nom, m.prenom);
    const ajAfter = calcAjFinal(key);
    const ajBefore = ajustementsEffectifs[key] || 0;
    logs.push(`  │  ${key.padEnd(25)} ${String(cpt[key] || 0).padStart(3)} jours  aj.init=${ajBefore > 0 ? "+" : ""}${ajBefore}  →  aj.final=${ajAfter > 0 ? "+" : ""}${ajAfter}`);
  }
  logs.push("  └──────────────────────────────────");

  return {
    affectations,
    compteurs: cpt,
    compteursParMois: cptParMois,
    nbSwaps,
    logs,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// ANCIENNE FONCTION MONOLITHIQUE (conservée pour compatibilité, appelle les 3 phases)
// ═══════════════════════════════════════════════════════════════════════
export function genererAstreintesThoraciques({ moisList, medecins, desiderataParMois, ajustements = {}, joursFeries }) {
  const prepData = preparerDonnees({ moisList, medecins, desiderataParMois, joursFeries });
  const p1 = phase1_attribuerBlocs({ prepData });
  const p2 = phase2_attribuerNguyen({
    prepData,
    affectationsValidees: p1.affectationsBlocs,
    compteurs: { ...p1.compteurs },
    compteursParMois: JSON.parse(JSON.stringify(p1.compteursParMois)),
  });
  const affectationsAvantP3 = { ...p1.affectationsBlocs, ...p2.affectationsNguyen };
  const p3 = phase3_joursRestants({
    prepData,
    affectationsValidees: affectationsAvantP3,
    compteurs: { ...p2.compteurs },
    compteursParMois: JSON.parse(JSON.stringify(p2.compteursParMois)),
    ajustements,
  });

  const allLogs = [...prepData.logs, ...p1.logs, ...p2.logs, ...p3.logs];

  const interieursEquite = prepData.interieursEquite;
  const exterieurs = prepData.exterieurs;
  const nguyenMedKey = prepData.nguyenMedKey;
  const nguyenMed = prepData.nguyenMed;
  const finalCompteurs = p3.compteurs;
  const finalCompteursParMois = p3.compteursParMois;

  const ajustementsEffectifs = {};
  for (const m of interieursEquite) {
    ajustementsEffectifs[medKey(m.nom, m.prenom)] = ajustements[medKey(m.nom, m.prenom)] || 0;
  }

  const statsInterieurs = interieursEquite.map((m) => {
    const key = medKey(m.nom, m.prenom);
    return { nom: m.nom, prenom: m.prenom, origine: "interieur", quotite: m.quotite, total: finalCompteurs[key] || 0, parMois: finalCompteursParMois[key] || {}, ajustement: ajustementsEffectifs[key] || 0 };
  });
  const statsExterieurs = exterieurs.map((m) => {
    const key = medKey(m.nom, m.prenom);
    return { nom: m.nom, prenom: m.prenom, origine: "exterieur", quotite: m.quotite, total: finalCompteurs[key] || 0, parMois: finalCompteursParMois[key] || {} };
  });
  const statsNguyen = nguyenMedKey ? {
    nom: nguyenMed.nom, prenom: nguyenMed.prenom, origine: "interieur", quotite: nguyenMed.quotite,
    total: finalCompteurs[nguyenMedKey] || 0, parMois: finalCompteursParMois[nguyenMedKey] || {}, regle: "1 jeudi/mois (règle 5)",
  } : null;

  return {
    affectations: p3.toutesAffectations,
    blocs: prepData.blocs,
    statistiques: { interieurs: statsInterieurs, exterieurs: statsExterieurs, nguyen: statsNguyen, totalAttribue: p3.totalAttribue, totalNonAttribue: p3.totalNonAttribue, totalJours: prepData.tousLesJours.length },
    logs: allLogs,
    moisList,
  };
}
